// src/hooks/useData.js
import { useEffect, useState, useRef } from 'react';
import { useDataCache } from '../context/DataCacheProvider';

export default function useData(
  key, 
  loader, 
  { 
    ttlMs = 60_000, 
    deps = [],
    refetchOnMount = true,
    refetchOnWindowFocus = true,
  } = {}
) {
  const cache = useDataCache();
  const [data, setData] = useState(() => cache.get(key));
  const [loading, setLoading] = useState(data == null);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);
  const initialFetchDone = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    // Serve cached immediately
    const cached = cache.get(key);
    if (cached != null) {
      setData(cached);
      setLoading(false);
    }

    // Skip fetch if refetchOnMount is false and we already fetched
    if (!refetchOnMount && initialFetchDone.current && deps.length === 0) {
      setLoading(false);
      return;
    }

    setError(null);
    setLoading(true);
    initialFetchDone.current = true;

    cache.use(key, loader, { ttlMs })
      .then((d) => { 
        if (mountedRef.current) {
          setData(d);
          setError(null);
        }
      })
      .catch((e) => { 
        if (mountedRef.current) setError(e);
      })
      .finally(() => { 
        if (mountedRef.current) setLoading(false);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, ...deps]);

  return { data, loading, error };
}